import React, { useCallback, useEffect, useState } from "react";
import { fetchRenewals, recordDues, remindMember, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";

// Dues worklist for a gym/spa: who's overdue and who's coming due, in one place,
// with one-tap Record payment (renews + extends) and Remind. Answers "who hasn't
// paid?" and kills silent lapses.

const METHODS = ["cash", "telebirr", "cbe_birr", "pos", "other"];

const S = {
  en: {
    title: "Renewals & dues", sub: "Who's overdue and who's coming due — collect in a couple of taps.",
    overdue: "Overdue", due_soon: "Due soon", arrears: "owed", expected: "Expected dues",
    empty_over: "No overdue members. 🎉", empty_soon: "Nobody due in the next 2 weeks.",
    plan: "Plan", od_days: "{n}d overdue", due_in: "due in {n}d", reminded: "reminded",
    record: "Record payment", remind: "Remind", amount: "Amount", method: "Method",
    save: "Save & renew", cancel: "Cancel", phone: "Phone", none: "—", saved: "Renewed — paid through {d}",
  },
  am: {
    title: "እድሳት እና ክፍያ", sub: "ጊዜው ያለፈበት እና በቅርቡ የሚያልቅ — በጥቂት ንኪኪ ይሰብስቡ።",
    overdue: "ጊዜው ያለፈ", due_soon: "በቅርቡ የሚያልቅ", arrears: "ዕዳ", expected: "የሚጠበቅ ክፍያ",
    empty_over: "ጊዜው ያለፈበት አባል የለም። 🎉", empty_soon: "በሚቀጥሉት 2 ሳምንታት የሚያልቅ የለም።",
    plan: "ዕቅድ", od_days: "{n}ቀን አልፏል", due_in: "በ{n}ቀን ያልቃል", reminded: "ተጠይቋል",
    record: "ክፍያ መዝግብ", remind: "አስታውስ", amount: "መጠን", method: "ዘዴ",
    save: "አስቀምጥ እና አድስ", cancel: "ሰርዝ", phone: "ስልክ", none: "—", saved: "ታድሷል — እስከ {d} ተከፍሏል",
  },
  om: {
    title: "Haaromsa & kaffaltii", sub: "Eenyu akka yeroon darbe fi dhiyaate — tuqaalee muraasaan sassaabi.",
    overdue: "Yeroon darbe", due_soon: "Dhiyootti", arrears: "idaa", expected: "Kaffaltii eegamu",
    empty_over: "Miseensa yeroon darbe hin jiru. 🎉", empty_soon: "Torban 2 keessatti kan xumuramu hin jiru.",
    plan: "Karoora", od_days: "guyyaa {n} darbe", due_in: "guyyaa {n} keessatti", reminded: "yaadachiifame",
    record: "Kaffaltii galmeessi", remind: "Yaadachiisi", amount: "Hamma", method: "Mala",
    save: "Olkaa'i & haaromsi", cancel: "Dhiisi", phone: "Bilbila", none: "—", saved: "Haaromfame — hanga {d} kaffalame",
  },
};

export default function RenewalsPage() {
  const lang = (typeof window !== "undefined" && localStorage.getItem("lang")) || "en";
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (n) => formatPrice(Number(n || 0), cur);

  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [pay, setPay] = useState(null);      // {id, amount, method} inline form open
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try { setD(await fetchRenewals({ window: 14 })); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not load renewals."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPay = (m) => setPay({ id: m.id, amount: String(m.dues_amount || ""), method: "cash" });
  const doPay = async () => {
    setBusy(true); setErr("");
    try {
      const res = await recordDues(pay.id, { amount: Number(pay.amount) || 0, method: pay.method });
      setFlash(t("saved").replace("{d}", res.next_due));
      setPay(null);
      await load();
      setTimeout(() => setFlash(""), 3500);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not record the payment."); }
    finally { setBusy(false); }
  };
  const doRemind = async (m) => {
    try { await remindMember(m.id); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not remind."); }
  };

  const kpi = (label, value, opts = {}) => (
    <div style={{ flex: "1 1 150px", background: opts.bg || "#fff", border: "1px solid " + (opts.bd || "#eef0f3"), borderRadius: 12, padding: "12px 16px" }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 2, color: opts.color || "#1f2d3d" }}>{value}</div>
      {opts.sub && <div className="muted" style={{ fontSize: 11 }}>{opts.sub}</div>}
    </div>
  );

  const row = (m, overdue) => (
    <div key={m.id} style={{ background: "#fff", border: "1px solid #eef0f3", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px" }}>
          <div style={{ fontWeight: 600 }}>{m.name || t("none")}</div>
          <div className="muted" style={{ fontSize: 12 }}>{m.plan_name} · {money(m.dues_amount)}{m.phone ? ` · ${m.phone}` : ""}</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: overdue ? "#b02a2a" : "#b8860b" }}>
          {overdue ? t("od_days").replace("{n}", m.days_overdue) : t("due_in").replace("{n}", m.due_in_days)}
          {overdue && m.arrears ? ` · ${money(m.arrears)} ${t("arrears")}` : ""}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-primary" onClick={() => openPay(m)} style={{ padding: "5px 10px" }}>{t("record")}</button>
          <button className="btn" onClick={() => doRemind(m)} style={{ padding: "5px 10px" }}>
            {t("remind")}{m.last_reminded_at ? " ✓" : ""}
          </button>
        </div>
      </div>
      {pay && pay.id === m.id && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 10, flexWrap: "wrap", borderTop: "1px solid #f2f4f6", paddingTop: 10 }}>
          <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12 }}>{t("amount")}</label>
            <input type="number" min="0" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} style={{ padding: 8, width: 120 }} /></div>
          <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12 }}>{t("method")}</label>
            <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} style={{ padding: 8 }}>
              {METHODS.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
            </select></div>
          <button className="btn btn-primary" disabled={busy} onClick={doPay} style={{ padding: "7px 12px" }}>{t("save")}</button>
          <button className="btn" onClick={() => setPay(null)} style={{ padding: "7px 12px" }}>{t("cancel")}</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="page">
      <h1 style={{ marginBottom: 2 }}>💳 {t("title")}</h1>
      <p className="muted" style={{ marginTop: 0 }}>{t("sub")}</p>

      {err && <div style={{ background: "#fdecec", color: "#b02a2a", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>{err}</div>}
      {flash && <div style={{ background: "#f2fbf4", color: "#2b8a3e", border: "1px solid #cdeed5", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>✓ {flash}</div>}

      {d && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0 18px" }}>
            {kpi(t("overdue"), d.overdue_count, { color: d.overdue_count ? "#b02a2a" : "#1f2d3d", bg: d.overdue_count ? "#fdf4f4" : "#fff", bd: d.overdue_count ? "#f3c2c2" : "#eef0f3", sub: d.overdue_arrears ? `${money(d.overdue_arrears)} ${t("arrears")}` : "" })}
            {kpi(t("due_soon"), d.due_soon_count, { color: "#b8860b" })}
            {kpi(t("expected"), money(d.expected_dues))}
          </div>

          <h3 style={{ marginBottom: 8 }}>🔴 {t("overdue")} {d.overdue_count ? `(${d.overdue_count})` : ""}</h3>
          {d.overdue.length === 0 && <p className="muted">{t("empty_over")}</p>}
          {d.overdue.map((m) => row(m, true))}

          <h3 style={{ margin: "18px 0 8px" }}>🟡 {t("due_soon")} {d.due_soon_count ? `(${d.due_soon_count})` : ""}</h3>
          {d.due_soon.length === 0 && <p className="muted">{t("empty_soon")}</p>}
          {d.due_soon.map((m) => row(m, false))}
        </>
      )}
    </div>
  );
}
